import { Modal } from './ui/Modal';
import changelogRaw from '../../../../CHANGELOG.md?raw';

interface ChangelogModalProps {
  open: boolean;
  onClose: () => void;
}

// Simple markdown parser for changelog display
function parseChangelog(markdown: string): React.ReactNode {
  const lines = markdown.split('\n');
  const elements: React.ReactNode[] = [];
  let key = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Skip empty lines
    if (!line.trim()) {
      continue;
    }

    // Main title (# OnDeck Changelog)
    if (line.startsWith('# ')) {
      // Skip - we use modal title instead
      continue;
    }

    // Horizontal rule
    if (line.startsWith('---')) {
      elements.push(
        <hr key={key++} className="my-6 border-gray-200 dark:border-gray-700" />
      );
      continue;
    }

    // Version header (## v0.005 - December 19, 2025)
    if (line.startsWith('## ')) {
      const text = line.replace('## ', '');
      elements.push(
        <h2 key={key++} className="text-lg font-bold text-gray-900 dark:text-white mt-4 mb-2">
          {text}
        </h2>
      );
      continue;
    }

    // Section header (### What's New)
    if (line.startsWith('### ')) {
      const text = line.replace('### ', '');
      elements.push(
        <h3 key={key++} className="text-sm font-semibold text-ondeck-600 dark:text-ondeck-400 mt-4 mb-2 uppercase tracking-wide">
          {text}
        </h3>
      );
      continue;
    }

    // List item (- Item text)
    if (line.startsWith('- ')) {
      const text = line.replace('- ', '');
      // Parse bold text (**text**)
      const parts = text.split(/\*\*(.*?)\*\*/g);
      elements.push(
        <li key={key++} className="text-gray-700 dark:text-gray-300 ml-4 mb-1">
          {parts.map((part, idx) =>
            idx % 2 === 1 ? (
              <strong key={idx} className="font-semibold text-gray-900 dark:text-white">
                {part}
              </strong>
            ) : (
              part
            )
          )}
        </li>
      );
      continue;
    }

    // Regular paragraph
    if (line.trim()) {
      elements.push(
        <p key={key++} className="text-gray-600 dark:text-gray-400 mb-2">
          {line}
        </p>
      );
    }
  }

  return elements;
}

export function ChangelogModal({ open, onClose }: ChangelogModalProps) {
  return (
    <Modal open={open} onClose={onClose} title="What's New" size="lg">
      <div className="px-6 pb-6">
        <div className="prose prose-sm dark:prose-invert max-w-none">
          {parseChangelog(changelogRaw)}
        </div>
      </div>
    </Modal>
  );
}
